import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { FC, useState } from 'react';
import Typewriter from 'typewriter-effect';
import { useAppDispatch, useAppSelector } from '../../../../redux/app/hooks';
import { RootState } from '../../../../redux/app/store';
import { setSelectedTab } from '../../../../redux/features/app/appSlice';
import { AppTabs, FeatureKeys } from '../../../../redux/features/app/AppState';
import {
    resetRecentAutoRenameStatus,
    setSelectedConversation,
} from '../../../../redux/features/conversations/conversationsSlice';
import { getScopeIcon } from '../../../utils/IconUtils';
import { timestampToDateString } from '../../../utils/TextUtils';
import { DeleteChatDialog } from '../Dialogs/DeleteChatDialog';
import { HideChatDialog } from '../Dialogs/HideChatDialog';
import { RestoreChatDialog } from '../Dialogs/RestoreChatDialog';
import { ShareChatDialog } from '../Dialogs/ShareChatDialog';
import { EditChatName } from './EditChatName';
import { ListItemActions } from './ListItemActions';

interface IChatListItemProps {
    id: string;
    header: string;
    timestamp: number;
    recentAutoRename: boolean;
    preview: string | undefined;
    isSelected: boolean;
    isHidden: boolean;
    scope?: string;
    onSelectChat: () => void;
}

export const ChatListItem: FC<IChatListItemProps> = ({
    id,
    header,
    timestamp,
    recentAutoRename,
    preview,
    isSelected,
    isHidden,
    scope,
    onSelectChat,
}) => {
    const dispatch = useAppDispatch();
    const { features, isAutohideChatListEnabled } = useAppSelector((state: RootState) => state.app);

    const showPreview = !features[FeatureKeys.SimplifiedExperience].enabled && preview;
    const showActions = features[FeatureKeys.SimplifiedExperience].enabled;

    const [editingTitle, setEditingTitle] = useState(false);
    const [hideDialogOpen, setHideDialogOpen] = useState(false);
    const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [shareDialogOpen, setShareDialogOpen] = useState(false);

    const onClick = () => {
        dispatch(setSelectedConversation(id));
        dispatch(setSelectedTab(AppTabs.Chat));
        // Enable to auto collapse Chat List on selection of a chat item in mobile view
        // Only fire this event on mobile view
        if (window.innerWidth <= 768 || isAutohideChatListEnabled) {
            onSelectChat();
        }
    };

    const time = timestampToDateString(timestamp);

    const onTypingComplete = () => {
        dispatch(resetRecentAutoRenameStatus({ chatId: id }));
    };

    return (
        <>
            <HideChatDialog
                chatId={id}
                chatName={header}
                open={hideDialogOpen}
                onClose={() => setHideDialogOpen(false)}
            />
            <RestoreChatDialog
                chatId={id}
                chatName={header}
                open={restoreDialogOpen}
                onClose={() => setRestoreDialogOpen(false)}
            />
            <DeleteChatDialog chatId={id} open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} />
            {features[FeatureKeys.MultiUserChat].enabled && shareDialogOpen && (
                <ShareChatDialog chatId={id} open={shareDialogOpen} onClose={() => setShareDialogOpen(false)} />
            )}
            <div
                className={`${isSelected ? 'bg-slate-300 dark:bg-slate-600 hover:bg-slate-300 dark:hover:bg-slate-600' : 'dark:hover:bg-slate-800'} flex group cursor-pointer items-center text-left rounded-lg space-x-2.5 p-3 mb-2 transition-colors duration-200 hover:bg-slate-200 focus:outline-none`}
                onClick={onClick}
                title={`Chat: ${header}`}
                data-chatitem={true}
                aria-label={`Chat list item: ${header}`}>
                <div className="w-7 h-7 flex">
                    <img src={getScopeIcon(scope)} alt="Bot Avatar" className="w-full h-full object-cover" />
                </div>
                {editingTitle ? (
                    <EditChatName
                        name={header}
                        chatId={id}
                        exitEdits={() => {
                            setEditingTitle(false);
                        }}
                    />
                ) : (
                    <>
                        <div className="flex-1 overflow-hidden w-8 content-center">
                            {isHidden && (
                                <span
                                    className={`rounded-full ${isSelected ? 'dark:bg-slate-700 bg-slate-200' : 'dark:bg-slate-600 bg-slate-300'} px-2.5 py-1 text-xs font-semibold leading-5 dark:text-slate-50 text-slate-600 sm:text-sm`}>
                                    Hidden
                                </span>
                            )}
                            <div>
                                <span title={header} className="text-sm">
                                    {recentAutoRename ? (
                                        <Typewriter
                                            options={{ delay: 20 }}
                                            onInit={(typewriter) => {
                                                typewriter
                                                    .typeString(header)
                                                    .callFunction(() => {
                                                        onTypingComplete();
                                                    })
                                                    .start();
                                            }}
                                        />
                                    ) : (
                                        <>
                                            <h1 className="text-sm font-medium capitalize truncate">{header}</h1>
                                            <p className="text-xs text-slate-500 dark:text-slate-400">{time}</p>
                                        </>
                                    )}
                                    {features[FeatureKeys.AzureContentSafety].enabled && (
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            strokeWidth={1.5}
                                            stroke="currentColor"
                                            className="w-4 h-4">
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                d="M12 9v3.75m0-10.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.75c0 5.592 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.57-.598-3.75h-.152c-3.196 0-6.1-1.25-8.25-3.286Zm0 13.036h.008v.008H12v-.008Z"
                                            />
                                        </svg>
                                    )}
                                </span>
                            </div>
                            {showPreview && (
                                <>
                                    {
                                        <span
                                            id={`message-preview-${id}`}
                                            className="block truncate leading-tight text-gray-500 dark:text-slate-500">
                                            {preview}
                                        </span>
                                    }
                                </>
                            )}
                        </div>
                        {showActions && (
                            <div className="invisible flex gap-8 items-center group-hover:visible">
                                <Popover>
                                    <PopoverButton className="block text-sm/6 text-primary-400 focus:outline-none data-[focus]:outline-1 data-[focus]:outline-white hover:text-primary-600 dark:text-slate-200 dark:hover:text-slate-400">
                                        <EllipsisVerticalIcon className="w-7 h-7" title="Actions" />
                                    </PopoverButton>
                                    <PopoverPanel
                                        transition
                                        anchor="bottom"
                                        className="divide-y divide-white/5 rounded-xl text-slate-700 bg-white dark:bg-slate-800 shadow-md text-sm/6 transition duration-200 ease-in-out [--anchor-gap:var(--spacing-5)] data-[closed]:-translate-y-1 data-[closed]:opacity-0 z-50">
                                        <div className="p-3">
                                            <ListItemActions
                                                chatId={id}
                                                isHidden={isHidden}
                                                onEditTitleClick={(e) => {
                                                    e.stopPropagation();
                                                    setEditingTitle(true);
                                                }}
                                                onHideChatClick={() => setHideDialogOpen(true)}
                                                onRestoreChatClick={() => setRestoreDialogOpen(true)}
                                                onDeleteChatClick={() => setDeleteDialogOpen(true)}
                                                onShareChatClick={() => setShareDialogOpen(true)}
                                            />
                                        </div>
                                    </PopoverPanel>
                                </Popover>
                            </div>
                        )}
                    </>
                )}
            </div>
        </>
    );
};
